import { Box, Switch } from '@mui/material'
import type { GridColDef, GridRenderCellParams, GridRowParams, GridSortModel } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import GridChipCell from '@/components/dataDisplay/GridChipCell'
import type { UserRole } from '@/constants/userRoles'
import { USER_ROLES } from '@/constants/userRoles'
import { useAlertContext } from '@/contexts/AlertContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { usePartnersQuery } from '@/features/partner/hooks/usePartnersQuery'
import { useSitesQuery } from '@/features/site/hooks/useSitesQuery'
import UsersDataGridToolbar from '@/features/user/components/UsersDataGridToolbar'
import { saveUser as apiSaveUser } from '@/features/user/endpoints/users'
import { useUsersQuery } from '@/features/user/hooks/useUsersQuery'
import type { User } from '@/features/user/types/user'
import { getAuthorizedRoles } from '@/features/user/utils/authorization'
import { getGridMultiselectHasAnyOfOperator } from '@/utils/datagrid/operators/hasAnyOf'

const DEFAULT_DATA_GRID_USERS_SORT_MODEL: GridSortModel = [{ field: 'username', sort: 'asc' }]

function Users() {
  const { loggedInUser } = useAuth()
  const { t } = useTranslation()

  const { mutate: saveUser } = useMutation({
    mutationFn: (variables: { user: User }) => apiSaveUser(variables.user),
    onSuccess: async (_, variables) => {
      const { isActive } = variables.user

      pushAlert({
        message: t(`users.user_is_now_${isActive ? 'enabled' : 'disabled'}_success_message`, {
          username: variables.user.username,
        }),
        severity: 'success',
      })
    },
  })

  const [showDisabledUsers, setShowDisabledUsers] = useState(false)
  const { pushAlert } = useAlertContext()

  const { sites, isFetching: isFetchingSites } = useSitesQuery()
  const { partners, isFetching: isFetchingPartners } = usePartnersQuery({ enabled: isCurrentUserAdmin() })
  const { users, isFetching: isFetchingUsers } = useUsersQuery({ partnerCode: loggedInUser?.partnerCode })

  const partnersMap = useMemo(() => new Map(partners?.map((i) => [i.partnerCode, i.name])), [partners])

  const siteOptions = useMemo(() => getUniqueSiteOptions(), [users, sites, showDisabledUsers])

  function getSite(siteUuid) {
    return sites?.find((c) => c.uuid === siteUuid)
  }

  function isCurrentUserAdmin() {
    return loggedInUser?.role === USER_ROLES.ADMINISTRATORS.value
  }

  function isCurrentUserPartnerAdmin() {
    return loggedInUser?.role === USER_ROLES.PARTNER_ADMINISTRATORS.value
  }

  function canMutateUsers() {
    return isCurrentUserAdmin() || isCurrentUserPartnerAdmin()
  }

  function getPartnerOptions() {
    const uniqueOptions = new Set<string>()
    for (const user of users ?? []) {
      if (user.partnerCode != null) {
        uniqueOptions.add(partnersMap.get(user.partnerCode) ?? user.partnerCode)
      }
    }
    return Array.from(uniqueOptions)
  }

  function getUniqueSiteOptions() {
    const seen = new Set<string>()
    const uniqueSiteOptions: string[] = []
    const visibleUsers = (showDisabledUsers ? users : users?.filter((user) => user.isActive)) ?? []

    for (const user of visibleUsers) {
      const roIds = user.allowedRoIds ?? []
      for (const roId of roIds) {
        if (!seen.has(roId)) {
          seen.add(roId)
          const site = getSite(roId)
          if (site !== undefined) {
            uniqueSiteOptions.push(site.name ?? `Unknown site [${site.uuid}]`)
          }
        }
      }
    }
    return uniqueSiteOptions
  }

  const columns: GridColDef[] = [
    ...(canMutateUsers()
      ? [
          {
            field: 'isActive',
            headerName: t('user.is_active.label'),
            sortable: false,
            renderCell: (params: GridRenderCellParams<User>) => (
              <Switch
                checked={params.row.isActive}
                color="primary"
                onChange={(event) => {
                  params.row.isActive = event.target.checked
                  saveUser({ user: { ...params.row, isActive: event.target.checked } })
                }}
                onClick={(event) => event.stopPropagation()}
              />
            ),
          } satisfies GridColDef,
        ]
      : []),
    {
      field: 'username',
      headerName: t('common.username'),
      flex: 1.5,
    },
    {
      field: 'role',
      headerName: t('users.role.label'),
      flex: 1.5,
      sortable: false,
      type: 'singleSelect',
      valueOptions: getAuthorizedRoles(loggedInUser?.role),
      getOptionLabel: (value) => (value ? t(`users.role.${value as UserRole}`) : '-'),
      renderCell: (params: GridRenderCellParams<User>) => {
        if (!params.row.role) {
          return ''
        }
        return (
          <Box>
            <GridChipCell key={params.row.role} label={t(`users.role.${params.row.role}`)} />
          </Box>
        )
      },
      valueGetter: (value: User['role']) => value ?? '',
    },
    {
      field: 'email',
      headerName: t('common.email'),
      flex: 2,
    },
    {
      field: 'allowedRoIds',
      headerName: t('users.column.customers'),
      flex: 1.5,
      sortable: false,
      filterOperators: [getGridMultiselectHasAnyOfOperator()],
      valueGetter: (value: User['allowedRoIds']) => {
        return value?.map((roId) => getSite(roId)?.name)
      },
      renderCell: (params: GridRenderCellParams<User>) => {
        const sites = params.row.allowedRoIds?.map(getSite).filter((site) => site !== undefined)

        return (
          <Box>
            {sites?.map((site) => (
              <GridChipCell key={site?.uuid} label={site?.name} />
            ))}
          </Box>
        )
      },
    },
    {
      field: 'partner',
      headerName: t('common.partner'),
      flex: 1.5,
      type: 'singleSelect',
      valueOptions: getPartnerOptions(),
      valueGetter: (_, { partnerCode }: User) => (partnerCode ? partnersMap.get(partnerCode) : partnerCode),
    },
  ]

  return (
    <CustomDataGrid
      clickableRows={
        canMutateUsers()
          ? {
              navigateTo: (params: GridRowParams<User>) => `/users/${params.row.id}`,
            }
          : undefined
      }
      columnVisibilityModel={{
        partner: isCurrentUserAdmin() || false,
      }}
      columns={columns}
      getRowId={(row) => row.id}
      initialState={{
        sorting: {
          sortModel: DEFAULT_DATA_GRID_USERS_SORT_MODEL,
        },
      }}
      isLoading={isFetchingUsers || isFetchingSites || isFetchingPartners}
      rows={(showDisabledUsers ? users : users?.filter((user) => user.isActive)) ?? []}
      slotProps={{
        toolbar: {
          showDisabledUsers: showDisabledUsers,
          authorizedRoles: getAuthorizedRoles(loggedInUser?.role),
          availableSiteOptions: siteOptions,
          showSiteFilter: isCurrentUserPartnerAdmin() || isCurrentUserAdmin(),
          showAddUserButton: canMutateUsers(),
          onToggleDisabledUsersClick: setShowDisabledUsers,
        },
      }}
      slots={{
        toolbar: UsersDataGridToolbar,
      }}
    />
  )
}

export default Users
